import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { CaretDown } from "components/icons/CaretDown";
import { useAnalytics } from "hooks/useAnalytics";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useEffect, useState } from "react";
import { CLICKED_ALL_FINANCIAL_YEARS_ON_FILINGS } from "constants/analyticsEvents";

type SelectFinancialYearProps = {
  seasons: { label: string; value: string }[];
};

export const SelectFinancialYear = ({ seasons }: SelectFinancialYearProps) => {
  const { update } = useUpdateQuery();
  const query = useQuery();
  const selectedSeasons = query.get("seasons");
  const { trackEvent } = useAnalytics();

  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(selectedSeasons ? selectedSeasons.split(",") : ["all"])
  );
  const [showDropdown, setShowDropdown] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackEvent(CLICKED_ALL_FINANCIAL_YEARS_ON_FILINGS);
    }
    setShowDropdown(open);
  };

  const selectAllOptions = (updated: Set<string>) => {
    updated.clear();
    seasons.map((season) => updated.add(season.value));
  };

  const handleToggleAll = (updated: Set<string>) => {
    if (updated.has("all")) {
      updated.clear();
    } else {
      selectAllOptions(updated);
    }
  };

  const handleUnselectOption = (updated: Set<string>, value: string) => {
    if (updated.has("all")) {
      updated.delete("all");
    }
    updated.delete(value);
  };

  const handleSelectOption = (updated: Set<string>, value: string) => {
    updated.add(value);
    if (updated.size + 1 === seasons.length) {
      selectAllOptions(updated);
    }
  };

  const updateQuery = (updated: Set<string>) => {
    update({
      query: "seasons",
      value: Array.from(updated).join(",") || null,
    });
  };

  const handleChange = (value: string) => {
    setSelectedOptions((prev) => {
      const updated = new Set(prev);

      if (value === "all") {
        handleToggleAll(updated);
      } else if (updated.has(value)) {
        handleUnselectOption(updated, value);
      } else {
        handleSelectOption(updated, value);
      }

      updateQuery(updated);

      return updated;
    });
  };

  useEffect(() => {
    if (selectedOptions.size === 0 || selectedOptions.has("all")) {
      const allOptionsValue = seasons.map((season) => season.value);
      setSelectedOptions(new Set(allOptionsValue));
    }
  }, [JSON.stringify(seasons)]);

  const title = selectedOptions.has("all")
    ? "All Financial Years"
    : Array.from(selectedOptions).length > 0
    ? Array.from(selectedOptions)
        .map(
          (val) => seasons.find((season) => season.value === val)?.label || ""
        )
        .join(", ")
    : "";

  const isOnlyOneSelected =
    selectedOptions.size === 1 ||
    (selectedOptions.has("all") && selectedOptions.size === 2);

  return (
    <DropDown.Root onOpenChange={handleOpenChange} open={showDropdown}>
      <DropDown.Trigger asChild className="t-group t-select-none">
        <div className="t-flex t-gap-3 t-rounded-md t-px-3 t-h-7 t-border t-border-solid t-border-neutral-10 t-bg-neutral-66 t-items-center">
          <CalendarIcon className="t-text-text-30" />
          <ToolTip text={title}>
            <div className="t-truncate t-w-28 t-text-body-sm t-text-text-60">
              {title}
            </div>
          </ToolTip>
          <div className="group-data-state-open:t-rotate-180 -t-mt-1 group-data-state-open:-t-mb-1">
            <CaretDown className="t-text-text-30 t-size-3" />
          </div>
        </div>
      </DropDown.Trigger>
      <DropDown.Portal>
        <DropDown.Content className="!t-min-w-24" sideOffset={8} align="start">
          {seasons.map((season) => {
            const isSelected = selectedOptions.has(season.value);
            const disable = isSelected && isOnlyOneSelected;
            const disableAllToDeselect = season.value === "all" && isSelected;

            return (
              <ConditionalToolTip
                key={season.value}
                condition={
                  (disable || disableAllToDeselect) &&
                  "At least one season is required."
                }
              >
                <div
                  className={classNames("t-px-3 t-py-2", {
                    "t-ml-4": season.value !== "all",
                  })}
                >
                  <Checkbox
                    label={season.label}
                    name={season.value}
                    checked={selectedOptions.has(season.value)}
                    onChange={() => handleChange(season.value)}
                    disabled={disable || disableAllToDeselect}
                  />
                </div>
              </ConditionalToolTip>
            );
          })}
        </DropDown.Content>
      </DropDown.Portal>
    </DropDown.Root>
  );
};
